import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  DrawerOverlay,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  VStack,
  HStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  FormControl,
  FormLabel,
  Tooltip,
  Flex,
} from '@chakra-ui/react';
import { Lock, X, Plus } from 'lucide-react';
// eslint-disable-next-line no-restricted-imports
import { Icon as ChakraIcon } from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { IconButton } from 'src/components/atoms/IconButton';
import { DrawerContainer } from 'src/components/molecules/DrawerContainer';
import {
  BrandTrackingConfig,
  useUpdateCompetitorsMutation,
} from 'src/api/brandTracking';

const MAX_COMPETITORS = 20;

const inputStyles = {
  bg: 'surface.panel',
  border: '1px solid',
  borderColor: 'surface.border',
  borderRadius: 'md',
  py: 3,
  px: 4,
  fontSize: '16px',
  fontFamily: 'marketingPage',
  _placeholder: { color: 'content.muted' },
  _focus: {
    borderColor: 'icon.green',
    boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
  },
};

const labelStyles = {
  fontFamily: 'marketingPage',
  fontSize: '16px',
  fontWeight: 500,
  color: 'content.title',
  mb: 3,
};

type KeywordEditorDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  config: BrandTrackingConfig;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
};

const normalizeKeyword = (value: string) => value.trim().toLowerCase();

export const KeywordEditorDrawer: React.FC<KeywordEditorDrawerProps> = ({
  isOpen,
  onClose,
  customerId,
  config,
  onSuccess,
  onError,
}) => {
  const [competitors, setCompetitors] = useState<string[]>(
    config.competitorKeywords,
  );
  const [newKeyword, setNewKeyword] = useState('');
  const updateMutation = useUpdateCompetitorsMutation(customerId);

  useEffect(() => {
    if (isOpen) {
      setCompetitors(config.competitorKeywords);
      setNewKeyword('');
    }
  }, [isOpen, config.competitorKeywords]);

  const handleAdd = () => {
    const keyword = normalizeKeyword(newKeyword);
    if (!keyword) return;
    if (keyword === normalizeKeyword(config.brandKeyword ?? '')) return;
    if (competitors.includes(keyword)) {
      setNewKeyword('');
      return;
    }
    if (competitors.length >= MAX_COMPETITORS) return;
    setCompetitors([...competitors, keyword]);
    setNewKeyword('');
  };

  const handleRemove = (keyword: string) => {
    setCompetitors(competitors.filter((k) => k !== keyword));
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    handleAdd();
  };

  // Whatever is typed in the add-input but not yet confirmed with "+"
  // still counts — users expect Save to take the field as-is.
  const getCompetitorsToSave = () => {
    const pending = normalizeKeyword(newKeyword);
    if (
      !pending ||
      pending === normalizeKeyword(config.brandKeyword ?? '') ||
      competitors.includes(pending) ||
      competitors.length >= MAX_COMPETITORS
    ) {
      return competitors;
    }
    return [...competitors, pending];
  };

  const handleSave = () => {
    updateMutation.mutate(getCompetitorsToSave(), {
      onSuccess: (result) => {
        onSuccess(result.message);
        onClose();
      },
      onError: (error) => {
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        const message = (
          error as { response?: { data?: { message?: string } } }
        )?.response?.data?.message;
        if (status === 403) {
          onError('Admin access is required to edit keywords.');
        } else {
          onError(message ?? 'Could not save keywords. Please try again.');
        }
      },
    });
  };

  const canSave =
    (competitors.length > 0 || normalizeKeyword(newKeyword).length > 0) &&
    !updateMutation.isLoading;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContainer>
        <DrawerCloseButton />
        <DrawerHeader pb={4} pt={12}>
          <Text
            fontFamily="marketingPage"
            fontWeight="bold"
            fontSize="32px"
            color="content.title">
            Tracked Keywords
          </Text>
        </DrawerHeader>

        <DrawerBody p={6} overflowY="auto">
          <VStack align="stretch" spacing={6}>
            {/* Brand keyword — locked */}
            <FormControl>
              <FormLabel {...labelStyles}>Your Brand</FormLabel>
              <Tooltip label="Contact support to change your brand keyword">
                <InputGroup>
                  <Input
                    value={config.brandKeyword ?? '—'}
                    isReadOnly
                    {...inputStyles}
                    bg="surface.sidebarMuted"
                    cursor="not-allowed"
                    _focus={{
                      boxShadow: 'none',
                      borderColor: 'surface.border',
                    }}
                  />
                  <InputRightElement>
                    <ChakraIcon as={Lock} color="content.muted" />
                  </InputRightElement>
                </InputGroup>
              </Tooltip>
            </FormControl>

            {/* Competitors */}
            <FormControl>
              <FormLabel {...labelStyles}>Competitors</FormLabel>
              <VStack align="stretch" spacing={2}>
                {competitors.length === 0 && (
                  <Text
                    fontSize="sm"
                    fontFamily="marketingPage"
                    color="content.muted">
                    Add at least one competitor keyword to track.
                  </Text>
                )}
                {competitors.map((keyword) => (
                  <Flex
                    key={keyword}
                    justify="space-between"
                    align="center"
                    border="1px solid"
                    borderColor="surface.border"
                    borderRadius="md"
                    pl={4}
                    pr={2}
                    py={1}>
                    <Text fontFamily="marketingPage">{keyword}</Text>
                    <IconButton
                      aria-label={`Remove ${keyword}`}
                      icon={<ChakraIcon as={X} boxSize={4} />}
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemove(keyword)}
                    />
                  </Flex>
                ))}
                <HStack>
                  <Input
                    placeholder="e.g. BMW"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    {...inputStyles}
                  />
                  <IconButton
                    aria-label="Add competitor"
                    icon={<ChakraIcon as={Plus} boxSize={5} />}
                    variant="outline"
                    borderColor="surface.border"
                    onClick={handleAdd}
                    isDisabled={
                      !newKeyword.trim() ||
                      competitors.length >= MAX_COMPETITORS
                    }
                  />
                </HStack>
                {competitors.length >= MAX_COMPETITORS && (
                  <Text
                    fontSize="sm"
                    fontFamily="marketingPage"
                    color="content.muted">
                    You can track up to {MAX_COMPETITORS} competitors.
                  </Text>
                )}
              </VStack>
            </FormControl>

            <Text
              fontSize="sm"
              fontFamily="marketingPage"
              color="content.muted">
              New keywords start collecting data after the next daily fetch and
              backfill automatically — charts may take a day or two to reflect
              the change.
            </Text>
          </VStack>
        </DrawerBody>

        <Box p={6} display="flex" gap={4}>
          <Button
            bg="surface.sidebarMuted"
            color="content.label"
            size="md"
            px={6}
            py={2}
            borderRadius="md"
            fontWeight="600"
            buttonName="cancel-tracked-keywords"
            fontFamily="marketingPage"
            onClick={onClose}
            flex={1}
            _hover={{ bg: 'sidebar.itemHoverBg' }}>
            Cancel
          </Button>
          <Button
            bg="icon.green"
            color="white"
            size="md"
            px={6}
            py={2}
            borderRadius="md"
            fontWeight="600"
            buttonName="save-tracked-keywords"
            fontFamily="marketingPage"
            onClick={handleSave}
            flex={1}
            isLoading={updateMutation.isLoading}
            isDisabled={!canSave}
            _hover={{ bg: 'green.600' }}>
            Save Changes
          </Button>
        </Box>
      </DrawerContainer>
    </Drawer>
  );
};
